import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Box } from 'components/common/box';
import { Owners, TitleWithIcon } from 'components/common';
import { StatItem } from 'components/common/stats';
import { AnalyticsDataWithChart } from '.';

import { AnalyticsPath } from './AnalyticsPaths';

interface Props {
  path: AnalyticsPath;
}

const AnalyticsPathsPath = ({ path }: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div className="path">
      <Box>
        <div className="path-content">
          <div className="path-title">
            <TitleWithIcon
              icon="tasks"
              title={path.title}
              titleLink={{
                as: `/admin/paths/${path.id}`,
                href: '/admin/paths/[pathId]'
              }}
            >
              <Owners owners={path.owners} />
            </TitleWithIcon>
          </div>

          <div className="path-stats">
            <div className="row-md row-center">
              <div className="col-min">
                <div className="courses">
                  <StatItem
                    align="left"
                    horizontal={true}
                    small={true}
                    label={messages.pathsSteps}
                    value={path.stats.numberOfSteps}
                  />
                </div>
                <div className="participants">
                  <StatItem
                    align="left"
                    horizontal={true}
                    small={true}
                    label={messages.commonParticipants}
                    value={path.stats.numberOfParticipants}
                  />
                </div>
              </div>

              <div className="col-min">
                <div className="participants-stats">
                  <AnalyticsDataWithChart
                    chartData={[
                      {
                        name: messages.commonCompleted,
                        value: path.stats.participantsCompleted,
                        tooltipValue:
                          path.stats.participantsCompleted +
                          ` (${Math.round(
                            (path.stats.participantsCompleted /
                              path.stats.numberOfParticipants) *
                              100
                          )}%)`,
                        color: theme.successStandaloneColor
                      },
                      {
                        name: messages.commonStarted,
                        value: path.stats.participantsStarted,
                        tooltipValue:
                          path.stats.participantsStarted +
                          ` (${Math.round(
                            (path.stats.participantsStarted /
                              path.stats.numberOfParticipants) *
                              100
                          )}%)`,
                        color: 'rgba(0, 0, 0, 0.4)'
                      },
                      {
                        name: messages.commonNotStarted,
                        value: path.stats.participantsNotStarted,
                        tooltipValue:
                          path.stats.participantsNotStarted +
                          ` (${Math.round(
                            (path.stats.participantsNotStarted /
                              path.stats.numberOfParticipants) *
                              100
                          )}%)`,
                        color: theme.lightGrey
                      }
                    ]}
                    chartEmptyTooltip={messages.commonNoParticipants}
                    chartId={`analytics-paths-completions-chart-${path.id}`}
                    chartLabel={
                      path.stats.numberOfParticipants
                        ? `${Math.round(
                            (path.stats.participantsCompleted /
                              path.stats.numberOfParticipants) *
                              100
                          )}%`
                        : '–'
                    }
                    statsSecondaryItems={[
                      {
                        category: 'success-light',
                        label: messages.commonCompleted,
                        value: path.stats.participantsCompleted
                      },
                      {
                        category: 'darker',
                        label: messages.commonStarted,
                        value: path.stats.participantsStarted
                      }
                    ]}
                  />
                </div>
              </div>

              <div className="col-min">
                <div className="deadlines">
                  <AnalyticsDataWithChart
                    chartData={[
                      {
                        name: messages.analyticsDeadlinesCompletedOnTime,
                        value: path.stats.deadlinesCompletedOnTime,
                        tooltipValue:
                          path.stats.deadlinesCompletedOnTime +
                          ` (${Math.round(
                            (path.stats.deadlinesCompletedOnTime /
                              path.stats.deadlines) *
                              100
                          )}%)`,
                        color: theme.successStandaloneColor
                      },
                      {
                        name: messages.analyticsDeadlinesCompletedLate,
                        value: path.stats.deadlinesCompletedLate,
                        tooltipValue:
                          path.stats.deadlinesCompletedLate +
                          ` (${Math.round(
                            (path.stats.deadlinesCompletedLate /
                              path.stats.deadlines) *
                              100
                          )}%)`,
                        color: 'rgba(0, 0, 0, 0.4)'
                      },
                      {
                        name: messages.analyticsDeadlinesLate,
                        value: path.stats.deadlinesLate,
                        tooltipValue:
                          path.stats.deadlinesLate +
                          ` (${Math.round(
                            (path.stats.deadlinesLate / path.stats.deadlines) *
                              100
                          )}%)`,
                        color: theme.dangerColor
                      },
                      {
                        name: messages.analyticsDeadlinesAhead,
                        value: path.stats.deadlinesAhead,
                        tooltipValue:
                          path.stats.deadlinesAhead +
                          ` (${Math.round(
                            (path.stats.deadlinesAhead / path.stats.deadlines) *
                              100
                          )}%)`,
                        color: theme.lightGrey
                      }
                    ]}
                    chartEmptyTooltip={messages.analyticsPathsDadlinesEmpty}
                    chartId={`analytics-paths-deadlines-chart-${path.id}`}
                    chartLabel={
                      path.stats.deadlines
                        ? `${Math.round(
                            (path.stats.deadlinesCompletedOnTime /
                              path.stats.deadlines) *
                              100
                          )}%`
                        : '–'
                    }
                    statsSecondaryItems={[
                      {
                        category: 'success-light',
                        label: messages.analyticsDeadlinesCompletedOnTime,
                        value: path.stats.deadlinesCompletedOnTime
                      },
                      {
                        category: 'error-light',
                        label: messages.analyticsDeadlinesLate,
                        value: path.stats.deadlinesLate
                      }
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Box>

      <style jsx>{`
        .path:not(:first-child) {
          margin-top: 20px;
        }
        .courses {
          display: block;
          margin-right: 20px;
          margin-top: 20px;
          padding-left: 4px;
        }
        .participants {
          display: block;
          margin-right: 20px;
          margin-top: 5px;
          padding-left: 4px;
        }
        .participants-stats {
          margin-right: 20px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .courses {
            margin-top: 0;
          }
          .path-content {
            align-items: center;
            display: flex;
          }
          .path-title {
            flex-grow: 1;
            min-width: 0;
          }
          .path-stats {
            flex-shrink: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsPathsPath;
